import { useNavigate } from 'react-router-dom';
import { areaCultivarLabel, isMixedArea } from '@/utils/areaCultivars';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import { Sprout, Trees, Calendar, Ruler, Eye, Edit, CalendarRange } from 'lucide-react';
import { useOrgTimezone, formatLocalDateInTz } from '@/utils/timezone';
import type { CultivarArea } from '../../types/orchard';

interface CultivarAreaCardProps {
  area: CultivarArea;
  showBlockName?: boolean;
}

function CultivarAreaCard({ area, showBlockName = false }: CultivarAreaCardProps) {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();

  const formatDate = (date: string | null) => {
    if (!date) return null;
    return formatLocalDateInTz(date, orgTz);
  };

  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-neutral-900 truncate">{area.name}</h3>
            {showBlockName && area.block_name && (
              <p className="text-xs text-neutral-500 truncate mt-0.5">Block: {area.block_name}</p>
            )}
          </div>
          <div className="flex-shrink-0 ml-3">
            <div className="h-9 w-9 rounded-lg bg-purple-100 flex items-center justify-center">
              <Sprout className="h-4 w-4 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Cultivar highlight */}
        <div className="bg-purple-50 rounded-lg p-2.5 mb-3">
          <p className="text-sm font-semibold text-purple-900 break-words line-clamp-2">
            {areaCultivarLabel(area) || (area.cultivars ? 'Nothing planted yet' : 'Unknown Cultivar')}
          </p>
          {isMixedArea(area) && (
            <p className="text-xs text-purple-600">Mixed block · {area.cultivars!.length} cultivars</p>
          )}
        </div>

        {/* Key stats grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {area.area_hectares != null && (
            <div className="flex items-center gap-1.5 text-sm text-neutral-600">
              <Ruler className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
              <span>{Number(area.area_hectares).toFixed(2)} ha</span>
            </div>
          )}
          {area.tree_count != null && (
            <div className="flex items-center gap-1.5 text-sm text-neutral-600">
              <Trees className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
              <span>{area.tree_count.toLocaleString()} trees</span>
            </div>
          )}
          {area.planting_date && (
            <div className="flex items-center gap-1.5 text-sm text-neutral-600">
              <Calendar className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
              <span>{formatDate(area.planting_date)}</span>
            </div>
          )}
          {area.training_system && (
            <div className="flex items-center gap-1.5 text-sm text-neutral-600">
              <Sprout className="h-3.5 w-3.5 text-neutral-400 flex-shrink-0" />
              <span className="truncate">{area.training_system}</span>
            </div>
          )}
        </div>

        {/* Next harvest — the date spray planning checks withholding periods
            against, so it belongs where the block is being looked at. */}
        {area.upcoming_earliest_harvest_date && (
          <p className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mb-3">
            <CalendarRange className="h-3.5 w-3.5 flex-shrink-0" />
            <span>
              Earliest harvest {formatDate(area.upcoming_earliest_harvest_date)}
              {area.upcoming_harvest_status === 'estimate' && ' (estimate)'}
            </span>
          </p>
        )}

        {/* Spacing info */}
        {(area.row_spacing_meters || area.tree_spacing_meters) && (
          <p className="text-xs text-neutral-500 mb-3">
            Spacing: {area.row_spacing_meters}m rows × {area.tree_spacing_meters}m trees
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-neutral-100">
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(`/orchard/cultivar-areas/${area.id}`)}
            className="flex-1"
          >
            <Eye className="h-3.5 w-3.5" />
            View Details
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/orchard/cultivar-areas/${area.id}/edit`)}
          >
            <Edit className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default CultivarAreaCard;
